import React, { useEffect, useRef, useState, useCallback } from 'react';
import API_CONFIG from '../config';

const GoogleAddressAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "Enter your address",
  required = false,
  id = "address-autocomplete"
}) => {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const hiddenInputRef = useRef(null);
  const [apiKey, setApiKey] = useState(null);

  const initializeAutocomplete = useCallback(() => {
    if (!containerRef.current || !window.google) {
      console.log('Cannot initialize autocomplete - missing container or Google API');
      return;
    }

    console.log('Initializing Google Places Autocomplete');
    
    try {
      // Use a persistent input for stability
      if (!inputRef.current) {
        inputRef.current = document.createElement('input');
        inputRef.current.type = 'text';
        inputRef.current.id = id;
        inputRef.current.placeholder = placeholder;
        inputRef.current.autocomplete = 'new-password';
        inputRef.current.setAttribute('data-form-type', 'other');
        inputRef.current.setAttribute('data-lpignore', 'true');
        inputRef.current.setAttribute('data-1p-ignore', 'true');
        inputRef.current.style.width = '100%';
        inputRef.current.style.padding = '12px 16px';
        inputRef.current.style.border = '1px solid rgba(209,213,219,1)';
        inputRef.current.style.borderRadius = '8px';
        inputRef.current.style.fontSize = '16px';
        const container = containerRef.current;
        container.innerHTML = '';
        container.appendChild(inputRef.current);
        inputRef.current.addEventListener('input', (e) => onChange(e.target.value));
      }

      inputRef.current.value = value || '';

      // Initialize Google Places Autocomplete
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: 'ca' },
          fields: ['formatted_address', 'address_components', 'geometry']
        }
      );

      // Add listener for place selection
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        
        if (place && place.formatted_address) {
          onChange(place.formatted_address);
          if (inputRef.current) inputRef.current.value = place.formatted_address;
        }
      });
      
      console.log('Google Places Autocomplete initialized successfully');
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
    }
  }, [onChange, value, placeholder, id]);

  useEffect(() => {
    // Fetch Google API key from backend via the existing Google Maps embed endpoint
    const fetchApiKey = async () => {
      try {
        // Try the new dedicated endpoint first
        console.log('Attempting to fetch API key from dedicated endpoint...');
        const response = await fetch(`${API_CONFIG.BASE_URL}/google-api-key`);
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.apiKey) {
            console.log('Successfully retrieved API key from dedicated endpoint');
            setApiKey(data.apiKey);
            return;
          }
        } else {
          console.log('Dedicated endpoint not available (status:', response.status, '), falling back to embed endpoint');
        }
      } catch (error) {
        console.log('Dedicated endpoint failed:', error.message, '- falling back to embed endpoint');
      }
      
      // Fallback: Extract API key from the existing Google Maps embed endpoint
      try {
        console.log('Extracting API key from Google Maps embed endpoint...');
        const embedResponse = await fetch(`${API_CONFIG.BASE_URL}/google-maps-embed?location=Toronto`);
        
        if (!embedResponse.ok) {
          throw new Error(`Embed endpoint returned status: ${embedResponse.status}`);
        }
        
        const embedHtml = await embedResponse.text();
        
        // Extract API key from the HTML response
        const keyMatch = embedHtml.match(/key=([^&"]+)/);
        if (keyMatch && keyMatch[1]) {
          console.log('Successfully extracted Google API key from embed endpoint');
          setApiKey(keyMatch[1]);
        } else {
          console.error('Could not extract Google API key from embed endpoint');
          console.log('Embed HTML response (first 500 chars):', embedHtml.substring(0, 500));
        }
      } catch (fallbackError) {
        console.error('Fallback method also failed:', fallbackError);
      }
    };

    fetchApiKey();
  }, []);

  useEffect(() => {
    if (!apiKey) return;

    // Check if Google Maps API is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      initializeAutocomplete();
      return;
    }

    // Load Google Maps API if not already loaded
    const loadGoogleMapsAPI = () => {
      // Check if script is already being loaded
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        // Wait for it to load
        const checkLoaded = setInterval(() => {
          if (window.google && window.google.maps && window.google.maps.places) {
            clearInterval(checkLoaded);
            initializeAutocomplete();
          }
        }, 100);
        return;
      }

      console.log('Loading Google Maps API with key:', apiKey.substring(0, 10) + '...');
      
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        console.log('Google Maps API loaded successfully');
        initializeAutocomplete();
      };
      
      script.onerror = () => {
        console.error('Failed to load Google Maps API');
      };
      
      document.head.appendChild(script);
    };

    loadGoogleMapsAPI();
  }, [apiKey, initializeAutocomplete]);

  // Update the input value when the prop changes
  useEffect(() => {
    if (containerRef.current) {
      const input = containerRef.current.querySelector('input');
      if (input && input.value !== value) {
        input.value = value || '';
      }
    }
  }, [value]);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Chrome autofill bait fields (offscreen) to absorb history suggestions */}
      <input
        type="text"
        autoComplete="address-line1"
        name={`bait-${Date.now()}-addr1`}
        tabIndex="-1"
        aria-hidden="true"
        style={{ position: 'fixed', top: '-10000px', left: '-10000px', height: 0, width: 0, opacity: 0, pointerEvents: 'none' }}
      />
      <input
        type="text"
        autoComplete="address-line2"
        name={`bait-${Date.now()}-addr2`}
        tabIndex="-1"
        aria-hidden="true"
        style={{ position: 'fixed', top: '-10000px', left: '-10000px', height: 0, width: 0, opacity: 0, pointerEvents: 'none' }}
      />

      {/* Hidden input for form compatibility */}
      <input
        ref={hiddenInputRef}
        type="hidden"
        id={id}
        name={id}
        value={value || ''}
        required={required}
      />
      
      {/* Container for the dynamically created Google Places input */}
      <div 
        ref={containerRef}
        style={{ width: '100%' }}
        className="google-places-container"
      />
    </div>
  );
};

export default GoogleAddressAutocomplete;